import React, { memo } from "react";
import { useMeetingAppContext } from "../../../hooks/MeetingAppContextDef";
import { ParticipantView } from "./ParticipantView";
import { ReactComponent as EliseIcon } from "../../../assets/images/anushka-profile.svg";
// Import same profile images used in the previous component
import profileImg from "../../../assets/images/anushka-profile.svg";
import profileImgMale2 from "../../../assets/images/profiles/male2.png";
import profileImgMale3 from "../../../assets/images/profiles/male3.png";
import profileImgFemale1 from "../../../assets/images/profiles/female1.png";
//  Helper: Title Case
function toTitleCase(str = "") {
  return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

//  Helper: Base64url decode (if needed for other logic)
function base64urlDecode(str) {
  str += "=".repeat((4 - (str.length % 4)) % 4);
  str = str.replace(/-/g, "+").replace(/_/g, "/");
  const binaryString = atob(str);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

//  Memoized Participant View
const MemoizedParticipant = React.memo(
  ParticipantView,
  (prevProps, nextProps) => prevProps.participantId === nextProps.participantId
);

//  Bot Participant View
const BotParticipant = memo(
  ({ displayName = "Anushka", isSmallView = false }) => {
    return (
      <div
        className={`relative overflow-hidden ${
          isSmallView ? "rounded-full" : "rounded-lg"
        } video-cover h-full w-full`}
      >
        <div className="h-full w-full flex items-center justify-center bg-gray-750">
          <EliseIcon
            className="h-full w-full object-cover"
            aria-label={displayName}
          />
        </div>

        {isSmallView && (
          <div className="absolute bottom-0 right-0 h-4 w-4">
            <div className="relative flex h-3 w-8">
              <div className="animate-pulse mx-0.5 h-2 w-0.5 bg-green-500"></div>
              <div className="animate-pulse mx-0.5 h-3 w-0.5 bg-green-500"></div>
              <div className="animate-pulse mx-0.5 h-2 w-0.5 bg-green-500"></div>
            </div>
          </div>
        )}

        {!isSmallView && (
          <div
            className="absolute bottom-2 left-2 rounded-md flex items-center justify-center p-2"
            style={{
              backgroundColor: "#00000066",
              transition: "all 200ms linear",
              transform: "scale(1)",
            }}
          >
            <p className="text-sm text-white ml-0.5">{displayName}</p>
          </div>
        )}
      </div>
    );
  }
);

//  Custom Participant View (for normal or bot)
const CustomParticipantView = ({
  participantId,
  isBotParticipant = false,
  isSmallView = false,
  displayName,
}) => {
  if (isBotParticipant) {
    return (
      <BotParticipant displayName={displayName} isSmallView={isSmallView} />
    );
  }
  return (
    <ParticipantView participantId={participantId} isSmallView={isSmallView} />
  );
};

//  Memoized Custom View
const MemoizedCustomParticipant = React.memo(
  CustomParticipantView,
  (prevProps, nextProps) =>
    prevProps.participantId === nextProps.participantId &&
    prevProps.isBotParticipant === nextProps.isBotParticipant &&
    prevProps.isSmallView === nextProps.isSmallView &&
    prevProps.displayName === nextProps.displayName
);

//  Main Grid
function ParticipantGrid({
  participantIds,
  isPresenting,
  includeBotParticipant = true,
  isBotSpeaking = false,
  activePanelSpeaker = null,
}) {
  const { sideBarMode } = useMeetingAppContext();
  const isMobile = window.matchMedia(
    "only screen and (max-width: 768px)"
  ).matches;

  // Get panel details from session storage
  const panelDetails = JSON.parse(
    sessionStorage.getItem("panelDetails") || "{}"
  );
  const { panel, pnlf1, pnlf2, pnlm1, pnlm2 } = panelDetails || {};

  let thumbnailParticipants = [];

  // Panel Type (lowercase)
  const panelType = panel?.toLowerCase() || "pnlo";

  // Assign profile images based on panel type
  let assignedImages = [];
  if (panelType === "pnla") {
    assignedImages = [profileImgMale3, profileImgMale2, profileImg];
  } else if (panelType === "pnlb") {
    assignedImages = [profileImgMale3, profileImg, profileImgFemale1];
  } else {
    assignedImages = [profileImg]; // PNLO single case
  }

  // Build thumbnail participants
  if (panelType === "pnlo") {
    const interviewerName = toTitleCase(
      pnlf1 || pnlf2 || pnlm1 || pnlm2 || "Anushka"
    );
    thumbnailParticipants = [
      {
        id: "bot-participant-id",
        name: interviewerName,
        isBot: true,
        img: assignedImages[0],
      },
    ];
  } else {
    const members = Object.entries(panelDetails)
      .filter(([key, value]) => key.startsWith("pnl") && value)
      .map(([key, value], index) => ({
        id: key,
        name: toTitleCase(value),
        isBot: false,
        img: assignedImages[index % assignedImages.length],
      }));
    thumbnailParticipants = members;
  }

  // Main participant (first actual or bot)
  const mainParticipantId =
    participantIds.length > 0 ? participantIds[0] : "bot-participant-id";

  // Speaking Indicator
  const SpeakingIndicator = () => (
    <div className="absolute -top-1 -right-1 bg-green-500 rounded-full p-1 animate-pulse">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        className="text-white"
      >
        <path
          d="M12 2C13.1 2 14 2.9 14 4V12C14 13.1 13.1 14 12 14C10.9 14 10 13.1 10 12V4C10 2.9 10.9 2 12 2Z"
          fill="currentColor"
        />
        <path
          d="M19 10V12C19 15.87 15.87 19 12 19C8.13 19 5 15.87 5 12V10H7V12C7 14.76 9.24 17 12 17C14.76 17 17 14.76 17 12V10H19Z"
          fill="currentColor"
        />
        <path d="M12 19V22H8V24H16V22H12V19Z" fill="currentColor" />
      </svg>
    </div>
  );

  return (
    <div className="flex flex-col h-full w-full relative">
      {/* Main participant */}
      <div className="absolute inset-0 p-2">
        <div className="h-full w-full">
          <MemoizedCustomParticipant
            participantId={mainParticipantId}
            isBotParticipant={mainParticipantId === "bot-participant-id"}
            isSmallView={false}
            displayName={
              panelType === "pnlo"
                ? toTitleCase(pnlf1 || pnlf2 || pnlm1 || pnlm2 || "Anushka")
                : "Anushka"
            }
          />
        </div>
      </div>

      {/* Thumbnail participants */}
      {thumbnailParticipants.length > 0 && (
        <div className="absolute bottom-4 right-4 flex flex-col gap-3">
          {thumbnailParticipants.map((participant, index) => {
            const { id, name, isBot, img } = participant;
            const isActive =
              name?.toLowerCase() === activePanelSpeaker?.toLowerCase();

            return (
              <div
                key={`thumbnail_${id}`}
                className={`relative rounded-lg border-width-3 ${
                  isActive && isBotSpeaking
                    ? "border-green-500 shadow-lg shadow-green-500/30"
                    : "border-white"
                } bg-[#111729] shadow-lg flex flex-col items-center justify-center ${
                  isBot ? "w-[212px] h-[210px]" : "w-[154px] h-[150px]"
                } transition-all duration-300 ease-in-out`}
              >
                {(isBot || isActive) && isBotSpeaking && <SpeakingIndicator />}

                {/* Profile Image */}
                <div
                  className={`${
                    isBot ? "w-32 h-32" : "w-20 h-20"
                  } rounded-full overflow-hidden`}
                >
                  <img
                    src={img}
                    alt={name}
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>

                <div className="mt-2 text-white text-allfonts-small font-medium text-center flex items-center gap-1">
                  {name}
                  {(isBot || isActive) && isBotSpeaking && (
                    <div className="flex space-x-0.5 ml-1">
                      <div
                        className="w-0.5 h-2 bg-[#08F423] animate-pulse rounded"
                        style={{ animationDuration: "1s" }}
                      ></div>
                      <div
                        className="w-0.5 h-3 bg-[#08F423] animate-pulse rounded"
                        style={{
                          animationDelay: "0.2s",
                          animationDuration: "1s",
                        }}
                      ></div>
                      <div
                        className="w-0.5 h-2 bg-[#08F423] animate-pulse rounded"
                        style={{
                          animationDelay: "0.4s",
                          animationDuration: "1s",
                        }}
                      ></div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const MemoizedParticipantGrid = React.memo(
  ParticipantGrid,
  (prevProps, nextProps) =>
    JSON.stringify(prevProps.participantIds) ===
      JSON.stringify(nextProps.participantIds) &&
    prevProps.isPresenting === nextProps.isPresenting &&
    prevProps.includeBotParticipant === nextProps.includeBotParticipant &&
    prevProps.isBotSpeaking === nextProps.isBotSpeaking &&
    prevProps.activePanelSpeaker === nextProps.activePanelSpeaker
);
